import FilterSelect from '../components/FilterSelect';
import { fmtDate } from '../utils/format';
import { logActivity } from '../utils/activityLog';
import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, RefreshCw, Search, LayoutDashboard, Users, FileText, BarChart2, Link2, Settings2, Building2, CreditCard, ShieldCheck, Filter, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Code2, X, GripVertical, CalendarDays } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { toast } from 'sonner';
import { getCurrentUser, hasPermission } from '../utils/roles';


const ROLES = [
  { value: 'admin',         label: 'Admin',          color: 'bg-red-500/20 text-red-400 border-red-500/30',           desc: 'Full access to everything' },
  { value: 'crm_manager',   label: 'CRM Manager',    color: 'bg-orange-500/20 text-orange-400 border-orange-500/30',  desc: 'Manages CRM operations' },
  { value: 'head_of_ret',   label: 'Head Of Ret',    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',        desc: 'Leads retention department' },
  { value: 'head_of_sales', label: 'Head Of Sales',  color: 'bg-green-500/20 text-green-400 border-green-500/30',     desc: 'Leads sales department' },
  { value: 'ret_team',      label: 'Ret Team',       color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',        desc: 'Retention team member' },
  { value: 'ret_agent',     label: 'Ret Agent',      color: 'bg-sky-500/20 text-sky-400 border-sky-500/30',           desc: 'Retention agent' },
  { value: 'sales_team',    label: 'Sales Team',     color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', desc: 'Sales team member' },
  { value: 'sales_agent',   label: 'Sales Agent',    color: 'bg-teal-500/20 text-teal-400 border-teal-500/30',        desc: 'Sales agent' },
];

const PERMISSIONS = [
  { section: 'Dashboard',     icon: LayoutDashboard, items: ['View Dashboard'] },
  { section: 'Clients',       icon: Users,           items: ['View Clients', 'Create Client', 'Edit Client', 'Delete Client', 'Import Clients', 'Export Clients', 'View Client Page', 'Change Client Status', 'Assign Clients'] },
  { section: 'Transactions',  icon: CreditCard,      items: ['View Transactions', 'Create Transaction', 'Edit Transaction', 'Delete Transaction', 'Export Transactions'] },
  { section: 'Documents',     icon: FileText,        items: ['View Documents', 'Upload Document', 'Approve Document', 'Delete Document'] },
  { section: 'Calendar',      icon: CalendarDays,    items: ['View Calendar'] },
  { section: 'Analytics',     icon: BarChart2,       items: ['View Analytics', 'View Finance'] },
  { section: 'Affiliates',    icon: Link2,           items: ['View Affiliates', 'Create Affiliate', 'Edit Affiliate', 'Delete Affiliate'] },
  { section: 'Users',         icon: ShieldCheck,     items: ['View Users', 'Create User', 'Edit User', 'Delete User'] },
  { section: 'Offices',       icon: Building2,       items: ['View Offices', 'Create Office', 'Edit Office', 'Delete Office'] },
  { section: 'Settings',      icon: Settings2,       items: ['View Settings', 'Edit Telephony Connections', 'Edit Logs', 'Edit Sessions', 'Edit IP Lists', 'Edit Permissions'] },
];

const DEFAULT_ROLE_PERMISSION_GRANTS = {
  admin: 'all',
  crm_manager: [
    'View Dashboard', 'View Clients', 'Create Client', 'Edit Client', 'Delete Client', 'Import Clients', 'Export Clients', 'Assign Clients',
    'View Client Page', 'Change Client Status', 'View Transactions', 'Create Transaction', 'Edit Transaction',
    'Delete Transaction', 'Export Transactions', 'View Documents', 'Upload Document', 'Approve Document', 'Delete Document',
    'View Calendar', 'View Analytics', 'View Finance', 'View Affiliates', 'View Users', 'View Offices', 'View Settings',
  ],
  head_of_sales: ['View Dashboard', 'View Clients', 'View Client Page', 'View Transactions', 'View Documents', 'View Calendar', 'View Finance', 'Assign Clients'],
  head_of_ret: ['View Dashboard', 'View Clients', 'View Client Page', 'View Transactions', 'View Documents', 'View Calendar', 'View Finance', 'Assign Clients'],
  sales_team: ['View Dashboard', 'View Clients', 'View Client Page', 'View Transactions', 'View Documents', 'View Calendar', 'Assign Clients'],
  ret_team: ['View Dashboard', 'View Clients', 'View Client Page', 'View Transactions', 'View Documents', 'View Calendar', 'Assign Clients'],
  sales_agent: [
    'View Dashboard', 'View Clients', 'Create Client', 'Edit Client', 'View Client Page', 'Change Client Status',
    'View Transactions', 'Create Transaction', 'View Documents', 'Upload Document', 'View Calendar',
  ],
  ret_agent: [
    'View Dashboard', 'View Clients', 'Edit Client', 'View Client Page', 'Change Client Status',
    'View Transactions', 'Create Transaction', 'View Documents', 'Upload Document', 'View Calendar',
  ],
};

const buildDefault = () => {
  const out = {};
  ROLES.forEach(r => {
    const grants = DEFAULT_ROLE_PERMISSION_GRANTS[r.value];
    const grantSet = grants === 'all' ? null : new Set(grants || []);
    out[r.value] = {};
    PERMISSIONS.forEach(s => s.items.forEach(item => {
      out[r.value][item] = grants === 'all' || grantSet.has(item);
    }));
  });
  return out;
};

const sanitizePermissions = (perms) => {
  const next = { ...perms };
  delete next.client;
  Object.values(next).forEach(rolePermissions => {
    if (!rolePermissions || typeof rolePermissions !== 'object') return;
    if (rolePermissions['Edit Telephony Connections'] === undefined && rolePermissions['Edit SIP Connections'] !== undefined) {
      rolePermissions['Edit Telephony Connections'] = rolePermissions['Edit SIP Connections'];
    }
    delete rolePermissions['Edit SIP Connections'];
  });
  return next;
};

// ── Color palette used for status badges ─────────────────────────────────────
export const STATUS_PALETTE = {
  blue:    'bg-blue-500/20 text-blue-400 border-blue-500/30',
  purple:  'bg-purple-500/20 text-purple-400 border-purple-500/30',
  indigo:  'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  cyan:    'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  green:   'bg-green-500/20 text-green-400 border-green-500/30',
  yellow:  'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  red:     'bg-red-500/20 text-red-400 border-red-500/30',
  orange:  'bg-orange-500/20 text-orange-400 border-orange-500/30',
  pink:    'bg-pink-500/20 text-pink-400 border-pink-500/30',
  teal:    'bg-teal-500/20 text-teal-400 border-teal-500/30',
  gray:    'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const COLOR_DOT = {
  blue:   'bg-blue-400',   purple: 'bg-purple-400', indigo: 'bg-indigo-400',
  cyan:   'bg-cyan-400',   green:  'bg-green-400',  yellow: 'bg-yellow-400',
  red:    'bg-red-400',    orange: 'bg-orange-400', pink:   'bg-pink-400',
  teal:   'bg-teal-400',   gray:   'bg-gray-400',
};

const ENTITY_TYPES = [
  { value: 'sales',     label: 'Sales' },
  { value: 'retention', label: 'Retention' },
];

const STATUS_ANALYTICS_GROUPS = [
  { value: 'new', label: 'New' },
  { value: 'valid', label: 'Valid' },
  { value: 'invalid', label: 'Invalid' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'converted', label: 'Converted' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'uncategorized', label: 'Uncategorized' },
];

const getStatusAnalyticsGroup = (value) => (
  STATUS_ANALYTICS_GROUPS.find(group => group.value === value) || STATUS_ANALYTICS_GROUPS[6]
);

const SETTINGS_TEXT_RU = {
  'Full access to everything': 'Полный доступ ко всему',
  'Manages CRM operations': 'Управляет CRM',
  'Leads retention department': 'Руководит Retention отделом',
  'Leads sales department': 'Руководит Sales отделом',
  'Retention team member': 'Участник Retention команды',
  'Retention agent': 'Retention агент',
  'Sales team member': 'Участник Sales команды',
  'Sales agent': 'Sales агент',
  Dashboard: 'Дашборд',
  Clients: 'Клиенты',
  Transactions: 'Транзакции',
  Documents: 'Документы',
  Calendar: 'Календарь',
  Analytics: 'Аналитика',
  Affiliates: 'Партнёры',
  Users: 'Пользователи',
  Offices: 'Офисы',
  Office: 'Офис',
  Settings: 'Настройки',
  'View Dashboard': 'Просмотр дашборда',
  'View Clients': 'Просмотр клиентов',
  'Create Client': 'Создание клиента',
  'Edit Client': 'Редактирование клиента',
  'Delete Client': 'Удаление клиента',
  'Import Clients': 'Импорт клиентов',
  'Export Clients': 'Экспорт клиентов',
  'View Client Page': 'Просмотр карточки клиента',
  'Change Client Status': 'Изменение статуса клиента',
  'Assign Clients': 'Назначение клиентов агентам',
  'View Transactions': 'Просмотр транзакций',
  'Create Transaction': 'Создание транзакции',
  'Edit Transaction': 'Редактирование транзакции',
  'Delete Transaction': 'Удаление транзакции',
  'Export Transactions': 'Экспорт транзакций',
  'View Documents': 'Просмотр документов',
  'Upload Document': 'Загрузка документа',
  'Approve Document': 'Одобрение документа',
  'Delete Document': 'Удаление документа',
  'View Calendar': 'Просмотр календаря',
  'View Analytics': 'Просмотр аналитики',
  'View Finance': 'Просмотр финансов',
  'View Affiliates': 'Просмотр партнёров',
  'Create Affiliate': 'Создание партнёра',
  'Edit Affiliate': 'Редактирование партнёра',
  'Delete Affiliate': 'Удаление партнёра',
  'View Users': 'Просмотр пользователей',
  'Create User': 'Создание пользователя',
  'Edit User': 'Редактирование пользователя',
  'Delete User': 'Удаление пользователя',
  'View Offices': 'Просмотр офисов',
  'Create Office': 'Создание офиса',
  'Edit Office': 'Редактирование офиса',
  'Delete Office': 'Удаление офиса',
  'View Settings': 'Просмотр настроек',
  'Edit Telephony Connections': 'Редактирование телефонии',
  'Edit Logs': 'Редактирование логов',
  'Edit Sessions': 'Редактирование сессий',
  'Edit IP Lists': 'Редактирование IP списков',
  'Edit Permissions': 'Редактирование прав',
  'All permissions': 'Все права',
  permissions: 'прав',
  of: 'из',
  granted: 'разрешено',
  'Save changes': 'Сохранить изменения',
  'Reset all': 'Сбросить всё',
  'Grant all': 'Разрешить всё',
  'Revoke all': 'Запретить всё',
  'Admin has full access to all features and cannot be restricted.': 'Admin имеет полный доступ ко всем функциям, его нельзя ограничить.',
  New: 'Новый',
  Edit: 'Изменить',
  Search: 'Поиск',
  'Clear All': 'Очистить всё',
  Loading: 'Загрузка',
  'No entries': 'Записей нет',
  Showing: 'Показано',
  entries: 'записей',
  'Added By': 'Добавил',
  'Apply To': 'Применить к',
  'All offices': 'Все офисы',
  'Current office': 'Текущий офис',
  Description: 'Описание',
  'Created At': 'Создано',
  Actions: 'Действия',
  'Edit IP': 'Изменить IP',
  'Add IP': 'Добавить IP',
  'IP Address': 'IP адрес',
  'User or department': 'Пользователь или отдел',
  'Optional note': 'Необязательная заметка',
  Cancel: 'Отмена',
  Save: 'Сохранить',
  Name: 'Название',
  Provider: 'Провайдер',
  Department: 'Отдел',
  Status: 'Статус',
  Active: 'Активен',
  Disabled: 'Отключён',
  'No data available': 'Данных нет',
  'Telephony connection': 'Подключение телефонии',
  'Enter Telephony Name...': 'Введите название телефонии...',
  'Please Select...': 'Выберите...',
  'Please select...': 'Выберите...',
  'API key': 'API ключ',
  missing: 'отсутствует',
  'Base URL': 'Base URL',
  Prefixes: 'Префиксы',
  'Outbound A number': 'Исходящий номер A',
  Optional: 'Необязательно',
  'No offices yet': 'Офисов пока нет',
  'Departments *': 'Отделы *',
  User: 'Пользователь',
  Role: 'Роль',
  'Login Time': 'Время входа',
  Expires: 'Истекает',
  'No active sessions': 'Активных сессий нет',
  'active sessions': 'активных сессий',
  'Search user...': 'Поиск пользователя...',
  'Log ID': 'ID лога',
  Date: 'Дата',
  'User ID': 'ID пользователя',
  'Made By': 'Кем сделано',
  Action: 'Действие',
  'Entity Type': 'Тип сущности',
  Result: 'Результат',
  'Entity ID': 'ID сущности',
  'All Action': 'Все действия',
  'Date From': 'Дата с',
  'Date To': 'Дата по',
  Apply: 'Применить',
  Reset: 'Сбросить',
  'No activity logs found': 'Логи активности не найдены',
  'No entries': 'Записей нет',
  hide: 'скрыть',
  success: 'успех',
  failed: 'ошибка',
  warning: 'предупреждение'
};

function useSettingsText() {
  const { language } = useLanguage();
  return (text) => (language === 'ru' ? SETTINGS_TEXT_RU[text] || text : text);
}

function StatusesTab() {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isDark = theme === 'dark';
  const [statuses, setStatuses]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [entityFilter, setEntityFilter] = useState('sales');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [form, setForm]           = useState({ name: '', color: 'blue', entity_type: 'sales', analytics_group: 'uncategorized', sort_order: 0, is_default: false });

  // drag state
  const dragIdx     = useRef(null);
  const dragOverIdx = useRef(null);
  const [dragOver, setDragOver] = useState(null); // index being hovered

  const cls = {
    th: `px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`,
    td: `px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`,
    input: `w-full px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-[#0f1729] border-gray-700 text-white placeholder-gray-600' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'}`,
    label: `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`,
    btn: `px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    btnPrimary: 'px-4 py-2 rounded-lg text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white transition-colors',
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings/statuses');
      setStatuses(data);
    } catch { toast.error(t('settings.statuses.failedLoad')); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  /* ── drag & drop handlers ───────────────────────────────────── */
  const handleDragStart = (i) => { dragIdx.current = i; };

  const handleDragEnter = (i) => {
    dragOverIdx.current = i;
    setDragOver(i);
  };

  const handleDragEnd = async () => {
    const from = dragIdx.current;
    const to   = dragOverIdx.current;
    dragIdx.current     = null;
    dragOverIdx.current = null;
    setDragOver(null);
    if (from === null || to === null || from === to) return;

    // build reordered filtered list
    const filtered = statuses.filter(s => s.entityType === entityFilter);
    const reordered = [...filtered];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);

    // assign sequential sort_order values starting from 0
    const updated = reordered.map((s, idx) => ({ ...s, sortOrder: idx }));

    // optimistic local update
    setStatuses(prev => {
      const others = prev.filter(s => s.entityType !== entityFilter);
      return [...others, ...updated];
    });

    // persist
    try {
      await api.post('/settings/statuses/reorder', {
        items: updated.map((s, idx) => ({ id: s.id, sort_order: idx })),
      });
    } catch {
      toast.error(t('settings.statuses.orderSaveFailed'));
      load(); // revert
    }
  };

  /* ── CRUD ──────────────────────────────────────────────────── */
  const openNew = () => {
    setEditItem(null);
    const filtered = statuses.filter(s => s.entityType === entityFilter);
    setForm({ name: '', color: 'blue', entity_type: entityFilter, analytics_group: 'uncategorized', sort_order: filtered.length, is_default: false });
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditItem(s);
    setForm({
      name: s.name,
      color: s.color,
      entity_type: s.entityType,
      analytics_group: s.analyticsGroup || 'uncategorized',
      sort_order: s.sortOrder,
      is_default: s.isDefault,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('settings.statuses.nameRequired')); return; }
    try {
      if (editItem) {
        await api.put(`/settings/statuses/${editItem.id}`, form);
        toast.success(t('settings.statuses.statusUpdated'));
      } else {
        await api.post('/settings/statuses', form);
        toast.success(t('settings.statuses.statusCreated'));
      }
      setShowModal(false);
      load();
    } catch { toast.error(t('settings.statuses.statusSaveFailed')); }
  };

  const remove = async (s) => {
    if (!window.confirm(t('settings.statuses.confirmDelete').replace('{name}', s.name))) return;
    try {
      await api.delete(`/settings/statuses/${s.id}`);
      toast.success(t('settings.statuses.deleted'));
      load();
    } catch { toast.error(t('settings.statuses.deleteFailed')); }
  };

  const filtered = statuses.filter(s => s.entityType === entityFilter);

  return (
    <div className="space-y-4">
      {/* Header: entity tabs + add button */}
      <div className="flex items-center justify-between gap-4">
        {/* Sales / Retention toggle */}
        <div className={`flex rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {ENTITY_TYPES.map(et => (
            <button
              key={et.value}
              onClick={() => setEntityFilter(et.value)}
              className={`px-5 py-2 text-sm font-medium transition-colors ${
                entityFilter === et.value
                  ? 'bg-purple-600 text-white'
                  : isDark
                    ? 'bg-[#0f1729] text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                    : 'bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {et.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
            {t('settings.statuses.dragPrefix')} <GripVertical size={12} className="inline -mt-0.5" /> {t('settings.statuses.dragSuffix')}
          </p>
          <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors">
            <Plus size={15} /> {t('settings.statuses.addStatus')}
          </button>
        </div>
      </div>

      {/* Drag-and-drop table */}
      <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className={`px-4 py-2.5 border-b flex items-center gap-2 ${isDark ? 'bg-[#0a0e1a] border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
          <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {ENTITY_TYPES.find(e => e.value === entityFilter)?.label} {t('settings.statuses.statusesTitle')}
          </span>
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${isDark ? 'bg-gray-800 text-gray-500' : 'bg-gray-200 text-gray-500'}`}>
            {filtered.length}
          </span>
        </div>
        <table className="w-full">
          <thead className={isDark ? 'bg-[#0f1729]' : 'bg-gray-50'}>
            <tr>
              <th className={`w-8 ${cls.th}`} />
              {['#', t('settings.statuses.columns.badge'), t('settings.statuses.columns.name'), 'Group', t('settings.statuses.columns.default'), t('settings.statuses.columns.actions')].map(h => (
                <th key={h} className={cls.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
            {loading ? (
              <tr>
                <td colSpan={7} className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {t('settings.statuses.loading')}
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {t('settings.statuses.empty')}
                </td>
              </tr>
            ) : filtered.map((s, i) => {
              const isOver = dragOver === i;
              return (
                <tr
                  key={s.id}
                  draggable
                  onDragStart={() => handleDragStart(i)}
                  onDragEnter={() => handleDragEnter(i)}
                  onDragEnd={handleDragEnd}
                  onDragOver={e => e.preventDefault()}
                  className={`transition-colors select-none ${
                    isOver
                      ? (isDark ? 'bg-purple-900/20 border-t-2 border-purple-500' : 'bg-purple-50 border-t-2 border-purple-400')
                      : (isDark ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50')
                  }`}
                >
                  {/* Drag handle */}
                  <td className={`${cls.td} w-8`}>
                    <GripVertical
                      size={15}
                      className={`cursor-grab active:cursor-grabbing ${isDark ? 'text-gray-600 hover:text-gray-400' : 'text-gray-300 hover:text-gray-500'}`}
                    />
                  </td>
                  {/* Position number */}
                  <td className={`${cls.td} w-8 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                    {i + 1}
                  </td>
                  {/* Badge */}
                  <td className={cls.td}>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_PALETTE[s.color] || STATUS_PALETTE.gray}`}>
                      {s.name}
                    </span>
                  </td>
                  {/* Name */}
                  <td className={`${cls.td} font-medium`}>{s.name}</td>
                  {/* Group */}
                  <td className={cls.td}>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${isDark ? 'border-gray-700 bg-gray-800/70 text-gray-300' : 'border-gray-200 bg-gray-100 text-gray-600'}`}>
                      {getStatusAnalyticsGroup(s.analyticsGroup).label}
                    </span>
                  </td>
                  {/* Default */}
                  <td className={cls.td}>
                    {s.isDefault && <span className="text-xs text-green-400 font-medium">✓ {t('settings.statuses.default')}</span>}
                  </td>
                  {/* Actions */}
                  <td className={cls.td}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(s)}
                        className={`p-1.5 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-purple-400 hover:bg-gray-700' : 'text-gray-500 hover:text-purple-600 hover:bg-purple-50'}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => remove(s)}
                        className={`p-1.5 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700' : 'text-gray-500 hover:text-red-600 hover:bg-red-50'}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
          {filtered.length} {t('settings.statuses.statusesCount')}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-sm rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{editItem ? t('settings.statuses.editStatus') : t('settings.statuses.newStatus')}</h2>
              <button onClick={() => setShowModal(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500'}><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* Name */}
              <div>
                <label className={cls.label}>{t('settings.statuses.name')} *</label>
                <input
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className={cls.input}
                  placeholder={t('settings.statuses.namePlaceholder')}
                />
              </div>
              {/* Color */}
              <div>
                <label className={cls.label}>{t('settings.statuses.color')}</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.keys(COLOR_DOT).map(c => (
                    <button
                      key={c}
                      onClick={() => setForm(p => ({ ...p, color: c }))}
                      title={c}
                      className={`w-7 h-7 rounded-full ${COLOR_DOT[c]} transition-transform ${form.color === c ? 'ring-2 ring-white scale-110' : 'opacity-60 hover:opacity-100'}`}
                    />
                  ))}
                </div>
                {form.name && (
                  <div className="mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_PALETTE[form.color] || STATUS_PALETTE.gray}`}>
                      {form.name}
                    </span>
                  </div>
                )}
              </div>
              {/* Analytics group */}
              <div>
                <label className={cls.label}>Analytics group</label>
                <select
                  value={form.analytics_group}
                  onChange={e => setForm(p => ({ ...p, analytics_group: e.target.value }))}
                  className={cls.input}
                >
                  {STATUS_ANALYTICS_GROUPS.map(group => (
                    <option key={group.value} value={group.value}>{group.label}</option>
                  ))}
                </select>
              </div>
              {/* Default */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_default}
                    onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))}
                    className="w-4 h-4 rounded accent-purple-500"
                  />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('settings.statuses.setAsDefault')}</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setShowModal(false)} className={cls.btn}>{t('settings.statuses.cancel')}</button>
                <button onClick={save} className={cls.btnPrimary}>{t('settings.statuses.save')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IpListTab({ listType }) {
  const { theme } = useTheme();
  const st = useSettingsText();
  const isDark = theme === 'dark';
  const currentUser = getCurrentUser();
  const policyOffice = currentUser.role === 'owner' ? st('All offices') : (currentUser.office || st('Current office'));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form, setForm] = useState({ ip: '', description: '' });

  const cls = {
    card: `rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`,
    input: `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`,
    label: `block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`,
    th: `px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`,
    td: `px-3 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`,
    btn: `px-3 py-1.5 text-sm border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    btnPrimary: 'px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors',
  };

  useEffect(() => { load(); }, [listType]);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/settings/ip/${listType}`);
      setEntries(data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const openNew = () => { setEditEntry(null); setForm({ ip: '', description: '' }); setShowModal(true); };
  const openEdit = (e) => { setEditEntry(e); setForm({ ip: e.ip, description: e.description || '' }); setShowModal(true); };

  const save = async () => {
    if (!form.ip) { toast.error('IP required'); return; }
    try {
      if (editEntry) {
        await api.put(`/settings/ip/${listType}/${editEntry.id}`, form);
        toast.success('Updated');
      } else {
        await api.post(`/settings/ip/${listType}`, form);
        toast.success('Added');
      }
      setShowModal(false); load();
    } catch { toast.error('Save failed'); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Remove IP ${e.ip}?`)) return;
    try { await api.delete(`/settings/ip/${listType}/${e.id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700">
            <Plus size={14} /> {st('New')}
          </button>
        </div>
        <button onClick={load} className={cls.btn}><RefreshCw size={14} /></button>
      </div>

      <div className={`${cls.card} overflow-hidden`}>
        <table className="w-full">
          <thead className={`border-b ${isDark ? 'bg-[#0a0e1a] border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
            <tr>
              <th className={cls.th}><input type="checkbox" className="w-3.5 h-3.5 accent-purple-500" /></th>
              {['IP', 'Added By', 'Apply To', 'Description', 'Created At', 'Actions'].map(h => (
                <th key={h} className={cls.th}>{st(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
            {loading ? (
              <tr><td colSpan={7} className={`${cls.td} text-center py-8`}>{st('Loading')}...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={7} className={`${cls.td} text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{st('No entries')}</td></tr>
            ) : entries.map(e => (
              <tr key={e.id} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                <td className={cls.td}><input type="checkbox" className="w-3.5 h-3.5 accent-purple-500" /></td>
                <td className={`${cls.td} font-mono text-sm`}>{e.ip}</td>
                <td className={cls.td}>{e.addedBy || '—'}</td>
                <td className={cls.td}>{e.office || st('All offices')}</td>
                <td className={cls.td}>{e.description || '—'}</td>
                <td className={cls.td}>{fmtDate(e.createdAt)}</td>
                <td className={cls.td}>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit(e)} className={`p-1.5 rounded ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}><Pencil size={13} /></button>
                    <button onClick={() => remove(e)} className="p-1.5 rounded text-red-400 hover:bg-red-500/10"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
          {st('Showing')} 1 - {entries.length} {st('of')} {entries.length} {st('entries')}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-sm rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{editEntry ? st('Edit IP') : st('Add IP')}</h2>
              <button onClick={() => setShowModal(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500'}>✕</button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div><label className={cls.label}>{st('IP Address')} *</label><input value={form.ip} onChange={e => setForm(p => ({ ...p, ip: e.target.value }))} className={cls.input} placeholder="e.g. 192.168.1.1" /></div>
              <div><label className={cls.label}>{st('Apply To')}</label><div className={`${cls.input} opacity-70 cursor-not-allowed`}>{editEntry?.office || policyOffice}</div></div>
              <div><label className={cls.label}>{st('Description')}</label><input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className={cls.input} placeholder={st('Optional note')} /></div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowModal(false)} className={cls.btn}>{st('Cancel')}</button>
                <button onClick={save} className={cls.btnPrimary}>{st('Save')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Toggle switch component
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      } ${checked ? 'bg-purple-600' : 'bg-gray-600'}`}
    >
      <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${
        checked ? 'translate-x-4' : 'translate-x-0'
      }`} />
    </button>
  );
}

function PermissionsTab() {
  const { theme } = useTheme();
  const st = useSettingsText();
  const isDark = theme === 'dark';

  const [roles, setRoles] = useState(ROLES);
  const [defaultPermissions, setDefaultPermissions] = useState(() => sanitizePermissions(buildDefault()));
  const [perms, setPerms] = useState(() => sanitizePermissions(buildDefault()));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState(ROLES[1].value); // default: CRM Manager
  const [collapsed, setCollapsed] = useState({});
  const [dirty, setDirty] = useState(false);

  const isAdmin = selectedRole === 'admin';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [permissionsRes, configRes] = await Promise.all([
          api.get('/settings/permissions'),
          api.get('/app/config').catch(() => ({ data: null })),
        ]);
        const config = configRes.data || {};
        const localByRole = Object.fromEntries(ROLES.map(role => [role.value, role]));
        const backendRoles = Array.isArray(config.roles) && Array.isArray(config.permissionRoles)
          ? config.roles
            .filter(role => config.permissionRoles.includes(role.value))
            .map(role => ({ ...localByRole[role.value], ...role, color: localByRole[role.value]?.color || 'bg-gray-500/20 text-gray-400 border-gray-500/30' }))
          : [];
        const backendDefaults = config.defaultPermissions ? sanitizePermissions(config.defaultPermissions) : null;
        const clean = sanitizePermissions(permissionsRes.data.permissions || {});
        if (!cancelled) {
          if (backendRoles.length) setRoles(backendRoles);
          if (backendDefaults) setDefaultPermissions(backendDefaults);
          setPerms(clean);
          setDirty(false);
        }
      } catch {
        toast.error('Failed to load permissions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const toggle = (item) => {
    if (isAdmin) return;
    setPerms(p => ({ ...p, [selectedRole]: { ...p[selectedRole], [item]: !p[selectedRole][item] } }));
    setDirty(true);
  };

  const toggleSection = (sectionItems, grant) => {
    if (isAdmin) return;
    setPerms(p => {
      const updated = { ...p[selectedRole] };
      sectionItems.forEach(item => { updated[item] = grant; });
      return { ...p, [selectedRole]: updated };
    });
    setDirty(true);
  };

  const save = async () => {
    const clean = sanitizePermissions(perms);
    try {
      setSaving(true);
      const { data } = await api.put('/settings/permissions', { permissions: clean });
      const saved = sanitizePermissions(data.permissions || clean);
      setPerms(saved);
      setDirty(false);
      toast.success('Permissions saved');
    } catch {
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const def = defaultPermissions || buildDefault();
    try {
      setSaving(true);
      const { data } = await api.put('/settings/permissions', { permissions: def });
      const saved = sanitizePermissions(data.permissions || def);
      setPerms(saved);
      setDirty(false);
      toast.success('Reset to defaults');
    } catch {
      toast.error('Failed to reset permissions');
    } finally {
      setSaving(false);
    }
  };

  const currentRole = roles.find(r => r.value === selectedRole) || roles[0] || ROLES[0];
  const rolePerms = perms[selectedRole] || {};
  const visiblePermissions = PERMISSIONS;
  const totalGranted = Object.values(rolePerms).filter(Boolean).length;
  const totalItems = PERMISSIONS.reduce((s, sec) => s + sec.items.length, 0);

  if (loading) {
    return (
      <div className={`rounded-lg border px-4 py-8 text-center text-sm ${isDark ? 'border-gray-800 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
        Loading permissions...
      </div>
    );
  }

  return (
    <div className="flex gap-4 h-full">

      {/* ── Left: role list ── */}
      <div className="w-56 shrink-0 space-y-1.5">
        {roles.map(r => {
          const rPerms = perms[r.value] || {};
          const roleCount = PERMISSIONS.reduce((s, sec) => s + sec.items.length, 0);
          const granted = Object.values(rPerms).filter(Boolean).length;
          const isSelected = selectedRole === r.value;
          return (
            <button key={r.value} onClick={() => setSelectedRole(r.value)}
              className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                isSelected
                  ? `${r.color} border-current`
                  : isDark ? 'bg-[#0f1729] border-gray-800 hover:border-gray-700 text-gray-300' : 'bg-white border-gray-200 hover:border-gray-300 text-gray-700'
              }`}>
              <div className="font-medium text-sm">{r.label}</div>
              <div className={`text-xs mt-0.5 ${isSelected ? 'opacity-70' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {r.value === 'admin' ? st('All permissions') : `${granted} / ${roleCount} ${st('permissions')}`}
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Right: permissions for selected role ── */}
      <div className="flex-1 min-w-0 space-y-3">

        {/* Role header */}
        <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 rounded-md text-sm font-semibold border ${currentRole.color}`}>{currentRole.label}</span>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{st(currentRole.desc)}</span>
            {!isAdmin && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                {totalGranted} {st('of')} {totalItems} {st('granted')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {dirty && <button onClick={save} disabled={saving} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-60">{saving ? 'Saving...' : st('Save changes')}</button>}
            <button onClick={reset} disabled={saving} className={`px-3 py-1.5 text-sm border rounded-md disabled:opacity-60 ${isDark ? 'border-gray-700 text-gray-400 hover:bg-[#1a1f3a]' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>{st('Reset all')}</button>
          </div>
        </div>

        {isAdmin && (
          <div className={`px-4 py-3 rounded-lg border text-sm ${isDark ? 'bg-green-900/10 border-green-800/30 text-green-400' : 'bg-green-50 border-green-200 text-green-700'}`}>
            {st('Admin has full access to all features and cannot be restricted.')}
          </div>
        )}

        {/* Permission sections */}
        {visiblePermissions.map(sec => {
          const Icon = sec.icon;
          const isCollapsed = collapsed[sec.section];
          const sectionGranted = sec.items.filter(i => rolePerms[i]).length;
          const allGranted = sectionGranted === sec.items.length;
          const noneGranted = sectionGranted === 0;

          return (
            <div key={sec.section} className={`rounded-lg border overflow-hidden ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`}>
              {/* Section header */}
              <div className={`flex items-center justify-between px-4 py-2.5 cursor-pointer select-none ${isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}`}
                onClick={() => setCollapsed(c => ({ ...c, [sec.section]: !c[sec.section] }))}>
                <div className="flex items-center gap-2.5">
                  <Icon size={15} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                  <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{st(sec.section)}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    allGranted ? (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
                    : noneGranted ? (isDark ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-500')
                    : (isDark ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700')
                  }`}>{sectionGranted}/{sec.items.length}</span>
                </div>
                <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
                  {!isAdmin && (
                    <>
                      <button onClick={() => toggleSection(sec.items, true)}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:text-green-400 hover:border-green-700' : 'border-gray-300 text-gray-500 hover:text-green-600 hover:border-green-400'}`}>
                        {st('Grant all')}
                      </button>
                      <button onClick={() => toggleSection(sec.items, false)}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:text-red-400 hover:border-red-700' : 'border-gray-300 text-gray-500 hover:text-red-500 hover:border-red-400'}`}>
                        {st('Revoke all')}
                      </button>
                    </>
                  )}
                  <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{isCollapsed ? '▸' : '▾'}</span>
                </div>
              </div>

              {/* Permission rows */}
              {!isCollapsed && (
                <div className={`border-t divide-y ${isDark ? 'border-gray-800 divide-gray-800' : 'border-gray-100 divide-gray-100'}`}>
                  {sec.items.map(item => (
                    <div key={item} className={`flex items-center justify-between px-4 py-2.5 ${!isAdmin ? (isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50') : ''}`}>
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{st(item)}</span>
                      <Toggle
                        checked={isAdmin ? true : (rolePerms[item] ?? false)}
                        onChange={() => toggle(item)}
                        disabled={isAdmin}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TelephonyConnectionsTab() {
  const { theme } = useTheme();
  const st = useSettingsText();
  const isDark = theme === 'dark';
  const currentUser = getCurrentUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const emptyForm = {
    name: '', apiKey: '', baseUrl: '', dialPrefixes: [],
    active: true
  };
  const [form, setForm] = useState(emptyForm);

  const cls = {
    card: `rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`,
    th: `px-3 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`,
    td: `px-3 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`,
    input: `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`,
    label: `block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`,
    btn: `px-3 py-1.5 text-sm border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    btnPrimary: 'px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors',
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      const { data } = await api.get('/settings/telephony-connections', { params });
      setRows(data || []);
    } catch {
      toast.error('Failed to load telephony connections');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditRow(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      name: row.name || '',
      apiKey: '',
      baseUrl: row.baseUrl || '',
      dialPrefixes: Array.isArray(row.dialPrefixes) ? row.dialPrefixes : [],
      active: row.active !== false,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!editRow && !form.apiKey.trim()) { toast.error('API key is required'); return; }
    if (!form.baseUrl.trim()) { toast.error('Base URL is required'); return; }
    try {
      if (editRow) {
        await api.put(`/settings/telephony-connections/${editRow.id}`, form);
        toast.success('Telephony connection updated');
      } else {
        await api.post('/settings/telephony-connections', form);
        toast.success('Telephony connection created');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save telephony connection');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete telephony connection "${row.name}"?`)) return;
    try {
      await api.delete(`/settings/telephony-connections/${row.id}`);
      toast.success('Telephony connection deleted');
      load();
    } catch {
      toast.error('Failed to delete telephony connection');
    }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/settings/telephony-connections/${row.id}/toggle`);
      load();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const updateDialPrefix = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      dialPrefixes: (prev.dialPrefixes || []).map((item, i) => (
        i === index ? { ...item, [field]: value } : item
      )),
    }));
  };

  const addDialPrefix = () => {
    setForm(prev => ({
      ...prev,
      dialPrefixes: [...(prev.dialPrefixes || []), { name: '', prefix: '' }],
    }));
  };

  const removeDialPrefix = (index) => {
    setForm(prev => ({
      ...prev,
      dialPrefixes: (prev.dialPrefixes || []).filter((_, i) => i !== index),
    }));
  };

  const chips = (items) => {
    if (!items || items.length === 0) return <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>All</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {items.map(item => (
          <span key={item} className={`rounded px-1.5 py-0.5 text-xs ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{item}</span>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button onClick={openNew} className="flex w-fit items-center gap-1.5 rounded-md bg-purple-600 px-4 py-1.5 text-sm text-white hover:bg-purple-700">
          <Plus size={14} /> {st('New')}
        </button>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className={`absolute left-3 top-2.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') load(); }}
              className={`${cls.input} w-64 pl-9`}
              placeholder={`${st('Search')} ${st('Telephony connection').toLowerCase()}...`}
            />
          </div>
          <button onClick={load} className={cls.btn}><RefreshCw size={14} /></button>
        </div>
      </div>

      <div className={`${cls.card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-[#0a0e1a] border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                {['ID', 'Name', 'API key', 'Offices', 'Created At', 'Status', 'Actions'].map(h => <th key={h} className={cls.th}>{st(h)}</th>)}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800/60' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={7} className={`${cls.td} py-10 text-center`}>{st('Loading')}...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className={`${cls.td} py-10 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{st('No data available')}</td></tr>
              ) : rows.map(row => (
                <tr key={row.id} className={isDark ? 'hover:bg-[#1a1f3a]/60' : 'hover:bg-gray-50'}>
                  <td className={`${cls.td} font-mono`}>#{row.id}</td>
                  <td className={`${cls.td} font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{row.name}</td>
                  <td className={cls.td}>
                    <div className={`text-xs ${row.hasApiKey ? 'text-green-400' : 'text-red-400'}`}>
                      {row.hasApiKey ? row.apiKeyMasked : st('missing')}
                    </div>
                    {(row.dialPrefixes || []).length > 0 && (
                      <div className={`mt-0.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {st('Prefixes')}: {(row.dialPrefixes || []).map(item => item.name || item.prefix || st('No prefix')).join(', ')}
                      </div>
                    )}
                  </td>
                  <td className={cls.td}>{chips(row.offices)}</td>
                  <td className={cls.td}>{fmtDate(row.createdAt)}</td>
                  <td className={cls.td}>
                    <button onClick={() => toggle(row)} className={`rounded-full px-2 py-0.5 text-xs font-medium ${row.active ? 'bg-green-500/15 text-green-400' : 'bg-gray-500/15 text-gray-400'}`}>
                      {row.active ? st('Active') : st('Disabled')}
                    </button>
                  </td>
                  <td className={cls.td}>
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(row)} className={`rounded p-1.5 ${isDark ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-gray-100'}`}><Pencil size={13} /></button>
                      <button onClick={() => remove(row)} className="rounded p-1.5 text-red-400 hover:bg-red-500/10"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className={`flex max-h-[calc(100vh-2rem)] w-full max-w-xl flex-col rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className={`flex items-center justify-between border-b px-6 py-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{editRow ? st('Edit') : `+ ${st('New')}`} {st('Telephony connection')}</h2>
              <button onClick={() => setShowModal(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500'}><X size={18} /></button>
            </div>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
              <div>
                <label className={cls.label}>{st('Name')} *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className={cls.input} placeholder={st('Enter Telephony Name...')} />
              </div>
              <div className={`rounded-md border p-3 ${isDark ? 'border-gray-700 bg-[#0a0e1a]' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className={cls.label}>{st('API key')} {editRow?.hasApiKey ? `(saved: ${editRow.apiKeyMasked})` : '*'}</label>
                      <input
                        type="password"
                        value={form.apiKey}
                        onChange={e => setForm(p => ({ ...p, apiKey: e.target.value }))}
                        className={cls.input}
                        placeholder={editRow?.hasApiKey ? 'Leave blank to keep current key' : 'Telephony API key'}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={cls.label}>{st('Base URL')} *</label>
                      <input value={form.baseUrl} onChange={e => setForm(p => ({ ...p, baseUrl: e.target.value }))} className={cls.input} placeholder="https://api.telephony.example" />
                    </div>
                    <div className="sm:col-span-2">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <label className={cls.label}>{st('Prefixes')}</label>
                        <button
                          type="button"
                          onClick={addDialPrefix}
                          className={`${cls.btn} flex h-8 w-10 items-center justify-center p-0`}
                          title={st('Add')}
                          aria-label={st('Add')}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <div className="space-y-2">
                        {(form.dialPrefixes || []).map((item, index) => (
                          <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                            <input
                              value={item.name || ''}
                              onChange={e => updateDialPrefix(index, 'name', e.target.value)}
                              className={cls.input}
                              placeholder="Name shown to agent"
                            />
                            <input
                              value={item.prefix || ''}
                              onChange={e => updateDialPrefix(index, 'prefix', e.target.value)}
                              className={cls.input}
                              placeholder="#02"
                            />
                            <button type="button" onClick={() => removeDialPrefix(index)} className="rounded p-2 text-red-400 hover:bg-red-500/10">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Add optional names and prefixes. A call without a prefix is always available.
                      </p>
                    </div>
                  </div>
              </div>
              <div>
                <label className={cls.label}>{st('Offices')}</label>
                <input
                  value={currentUser.office || (editRow?.offices || []).join(', ') || st('All offices')}
                  className={`${cls.input} cursor-not-allowed opacity-75`}
                  disabled
                  readOnly
                />
              </div>
            </div>
            <div className={`flex justify-end gap-2 border-t px-6 py-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button onClick={() => setShowModal(false)} className={cls.btn}>{st('Cancel')}</button>
              <button onClick={save} className={cls.btnPrimary}>{st('Save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SessionsTab() {
  const { theme } = useTheme();
  const st = useSettingsText();
  const isDark = theme === 'dark';
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const LIMIT = 25;

  const cls = {
    th: `px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`,
    td: `px-3 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`,
    btn: `px-3 py-1.5 text-sm border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    input: `px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`,
  };

  useEffect(() => { load(); }, [page]);

  const load = async () => {
    try {
      setLoading(true);
      const params = { page, limit: LIMIT };
      if (search) params.search = search;
      const { data } = await api.get('/settings/sessions', { params });
      setSessions(data.sessions || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch { toast.error('Failed to load sessions'); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Terminate this session?')) return;
    try { await api.delete(`/settings/sessions/${id}`); toast.success('Session terminated'); load(); }
    catch { toast.error('Failed'); }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear ALL sessions? This will log out all users.')) return;
    try { await api.delete('/settings/sessions'); toast.success('All sessions cleared'); load(); }
    catch { toast.error('Failed'); }
  };

  const roleBadge = (role) => {
    const colors = {
      owner:          'bg-purple-500/15 text-purple-400',
      admin:          'bg-red-500/15 text-red-400',
      crm_manager:    'bg-orange-500/15 text-orange-400',
      head_of_ret:    'bg-blue-500/15 text-blue-400',
      head_of_sales:  'bg-green-500/15 text-green-400',
      ret_team:       'bg-cyan-500/15 text-cyan-400',
      ret_agent:      'bg-sky-500/15 text-sky-400',
      sales_team:     'bg-emerald-500/15 text-emerald-400',
      sales_agent:    'bg-teal-500/15 text-teal-400',
    };
    const labels = {
      admin: 'Admin', crm_manager: 'CRM Manager',
      head_of_ret: 'Head Of Ret', head_of_sales: 'Head Of Sales',
      ret_team: 'Ret Team', ret_agent: 'Ret Agent',
      sales_team: 'Sales Team', sales_agent: 'Sales Agent',
      owner: 'Owner',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[role] || (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')}`}>
        {labels[role] || role || '—'}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <form onSubmit={e => { e.preventDefault(); setPage(1); load(); }} className="flex gap-2">
          <div className="relative">
            <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={st('Search user...')}
              className={`pl-9 pr-3 py-2 w-56 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`} />
          </div>
          <button type="submit" className={cls.btn}>{st('Search')}</button>
        </form>
        <div className="flex items-center gap-2">
          <button onClick={load} className={cls.btn}><RefreshCw size={14} /></button>
          <button onClick={clearAll} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-500/30 text-red-400 rounded-md hover:bg-red-500/10 transition-colors">
            <Trash2 size={14} /> {st('Clear All')}
          </button>
        </div>
      </div>

      <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`}>
        <table className="w-full">
          <thead className={`border-b ${isDark ? 'bg-[#0a0e1a] border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
            <tr>
              {['#', 'User', 'Role', 'Email', 'IP Address', 'Login Time', 'Expires', 'Actions'].map(h => (
                <th key={h} className={cls.th}>{st(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
            {loading ? (
              <tr><td colSpan={8} className={`${cls.td} text-center py-10`}>{st('Loading')}...</td></tr>
            ) : sessions.length === 0 ? (
              <tr><td colSpan={8} className={`${cls.td} text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{st('No active sessions')}</td></tr>
            ) : sessions.map((s, i) => (
              <tr key={s.id} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                <td className={`${cls.td} text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{(page - 1) * LIMIT + i + 1}</td>
                <td className={cls.td}><span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.userName}</span></td>
                <td className={cls.td}>{roleBadge(s.userRole)}</td>
                <td className={cls.td}>{s.userEmail}</td>
                <td className={`${cls.td} font-mono text-xs`}>{s.ipAddress || '—'}</td>
                <td className={cls.td}>{fmtDate(s.createdAt)}</td>
                <td className={cls.td}>{fmtDate(s.expiresAt)}</td>
                <td className={cls.td}>
                  <button onClick={() => remove(s.id)} className="p-1.5 rounded text-red-400 hover:bg-red-500/10">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className={`flex items-center justify-between px-4 py-2 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{total} {st('active sessions')}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className={`${cls.btn} disabled:opacity-40 py-1 px-2 text-xs`}>‹</button>
            <span className={`px-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{page}/{totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className={`${cls.btn} disabled:opacity-40 py-1 px-2 text-xs`}>›</button>
          </div>
        </div>
      </div>
    </div>
  );
}


function LogsTab() {
  const { theme } = useTheme();
  const st = useSettingsText();
  const isDark = theme === 'dark';

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedJson, setExpandedJson] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    action: [], dateFrom: '', dateTo: '', entity_type: [],
    made_by: [], office: [], department: [],
  });
  // Options loaded from server
  const [options, setOptions] = useState({ actions: [], entityTypes: [], offices: [], madeBy: [] });

  const sel = `w-full px-2 py-1.5 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  useEffect(() => {
    api.get('/settings/logs/options').then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [page, limit]);

  const load = async (nextFilters = filters, nextPage = page) => {
    try {
      setLoading(true);
      const params = { page: nextPage, limit };
      if (nextFilters.action.length)      params.action       = nextFilters.action;
      if (nextFilters.dateFrom)    params.date_from    = new Date(nextFilters.dateFrom).toISOString();
      if (nextFilters.dateTo)      params.date_to      = new Date(nextFilters.dateTo + 'T23:59:59').toISOString();
      if (nextFilters.entity_type.length) params.entity_type  = nextFilters.entity_type;
      if (nextFilters.made_by.length)     params.made_by      = nextFilters.made_by;
      if (nextFilters.office.length)      params.office       = nextFilters.office;
      if (nextFilters.department.length)  params.department   = nextFilters.department;
      const { data } = await api.get('/settings/logs', { params, paramsSerializer: { indexes: null } });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  };

  const applyFilters = () => { setPage(1); load(filters, 1); };
  const resetFilters = () => {
    const empty = { action: [], dateFrom: '', dateTo: '', entity_type: [], made_by: [], office: [], department: [] };
    setFilters(empty);
    setPage(1);
    load(empty, 1);
  };

  const resultBadge = (r) => {
    const map = {
      success: 'bg-green-500/20 text-green-400',
      failed:  'bg-red-500/20 text-red-400',
      warning: 'bg-yellow-500/20 text-yellow-400',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${map[r] || 'bg-gray-500/20 text-gray-400'}`}>
        {r || 'success'}
      </span>
    );
  };

  const formatJsonData = (value) => {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  };

  const th = `px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const td = `px-3 py-2.5 text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

  const from = (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);

  return (
    <div className="space-y-3">

      {/* ── Filters ── */}
      <div className={`rounded-lg border p-3 ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-2">
          {/* All Action */}
          <div>
            <p className={`text-xs mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{st('All Action')}</p>
            <FilterSelect isDark={isDark} aria-label="action" value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))} className={sel}>
              <option value="">{st('Please select...')}</option>
              {options.actions.map(a => <option key={a} value={a}>{a}</option>)}
            </FilterSelect>
          </div>
          {/* Date From */}
          <div>
            <p className={`text-xs mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{st('Date From')}</p>
            <input type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} className={sel} />
          </div>
          {/* Date To */}
          <div>
            <p className={`text-xs mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{st('Date To')}</p>
            <input type="date" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} className={sel} />
          </div>
          {/* Entity Type */}
          <div>
            <p className={`text-xs mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{st('Entity Type')}</p>
            <FilterSelect isDark={isDark} aria-label="entity_type" value={filters.entity_type} onChange={e => setFilters(f => ({ ...f, entity_type: e.target.value }))} className={sel}>
              <option value="">{st('Please select...')}</option>
              {options.entityTypes.map(e => <option key={e} value={e}>{e}</option>)}
            </FilterSelect>
          </div>
          {/* Made By */}
          <div>
            <p className={`text-xs mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{st('Made By')}</p>
            <FilterSelect isDark={isDark} aria-label="made_by" value={filters.made_by} onChange={e => setFilters(f => ({ ...f, made_by: e.target.value }))} className={sel}>
              <option value="">{st('Please select...')}</option>
              {options.madeBy.map(m => <option key={m} value={m}>{m}</option>)}
            </FilterSelect>
          </div>
          {/* Office */}
          <div>
            <p className={`text-xs mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{st('Office')}</p>
            <FilterSelect isDark={isDark} aria-label="office" value={filters.office} onChange={e => setFilters(f => ({ ...f, office: e.target.value }))} className={sel}>
              <option value="">{st('Please select...')}</option>
              {options.offices.map(o => <option key={o} value={o}>{o}</option>)}
            </FilterSelect>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={applyFilters} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700">
            <Filter size={12} /> {st('Apply')}
          </button>
          <button onClick={resetFilters} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-[#1a1f3a]' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
            <X size={12} /> {st('Reset')}
          </button>
          <button onClick={() => load()} className={`p-1.5 border rounded transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-[#1a1f3a]' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
            <RefreshCw size={13} />
          </button>
          <div className="flex-1" />
        </div>
      </div>

      {/* ── Table ── */}
      <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-[#0a0e1a] border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                {['Log ID','Date','IP','User ID','Made By','Role','Action','Description','Entity Type','Office','Department','Result','Entity ID','JSON'].map(h => (
                  <th key={h} className={th}>{st(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800/60' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={14} className={`${td} text-center py-10`}>{st('Loading')}...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={14} className={`${td} text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{st('No activity logs found')}</td></tr>
              ) : logs.map((l, i) => (
                <tr key={l.id} className={`transition-colors ${isDark ? 'hover:bg-[#1a1f3a]/60' : 'hover:bg-gray-50'}`}>
                  {/* Log ID */}
                  <td className={`${td} font-mono`}>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                      <span title={l.id}>#{l.id?.slice(0, 8) || (page - 1) * limit + i + 1}</span>
                    </span>
                  </td>
                  {/* Date */}
                  <td className={`${td} whitespace-nowrap`}>{fmtDate(l.createdAt)}</td>
                  {/* IP */}
                  <td className={`${td} font-mono text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{l.ipAddress || '—'}</td>
                  {/* User ID */}
                  <td className={td}>
                    {l.userId ? (
                      <span className={`font-mono text-xs ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>#{l.userId.slice(0,6)}</span>
                    ) : '—'}
                  </td>
                  {/* Made By */}
                  <td className={`${td} font-medium ${isDark ? 'text-white' : 'text-gray-900'} whitespace-nowrap`}>{l.userName || '—'}</td>
                  {/* Role */}
                  <td className={`${td} whitespace-nowrap`}>{l.userRole || '—'}</td>
                  {/* Action */}
                  <td className={td}>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-blue-500/15 text-blue-400' : 'bg-blue-50 text-blue-700'}`}>
                      {l.action}
                    </span>
                  </td>
                  {/* Description */}
                  <td className={`${td} min-w-[240px] max-w-md whitespace-normal`}>{l.description || '—'}</td>
                  {/* Entity Type */}
                  <td className={`${td} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{l.entityType || '—'}</td>
                  {/* Office */}
                  <td className={td}>{l.office || '—'}</td>
                  {/* Department */}
                  <td className={td}>{l.department || '—'}</td>
                  {/* Result */}
                  <td className={td}>{resultBadge(l.result)}</td>
                  {/* Entity ID */}
                  <td className={`${td} font-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {l.entityId ? `#${l.entityId.slice(0, 8)}` : '—'}
                  </td>
                  {/* JSON */}
                  <td className={td}>
                    {l.jsonData ? (
                      <button onClick={() => setExpandedJson(expandedJson === l.id ? null : l.id)}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs border transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-500' : 'border-gray-300 text-gray-500 hover:text-gray-700'}`}>
                        <Code2 size={11} />
                        {expandedJson === l.id ? st('hide') : '{ ... }'}
                      </button>
                    ) : <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>—</span>}
                    {expandedJson === l.id && l.jsonData && (
                      <pre className={`mt-1.5 p-2 rounded text-xs max-w-xs overflow-x-auto whitespace-pre-wrap break-all ${isDark ? 'bg-[#0a0e1a] text-green-400' : 'bg-gray-50 text-gray-700'}`}>
                        {formatJsonData(l.jsonData)}
                      </pre>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        <div className={`flex items-center justify-between px-4 py-2.5 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {total > 0 ? `${st('Showing')} ${from} - ${to} ${st('of')} ${total} ${st('entries')}` : st('No entries')}
            </span>
            <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
              className={`text-xs px-2 py-1 border rounded ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}>
              {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={page === 1}
              className={`p-1.5 border rounded disabled:opacity-40 transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-[#1a1f3a]' : 'border-gray-300 hover:bg-gray-50'}`}>
              <ChevronsLeft size={13} />
            </button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className={`p-1.5 border rounded disabled:opacity-40 transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-[#1a1f3a]' : 'border-gray-300 hover:bg-gray-50'}`}>
              <ChevronLeft size={13} />
            </button>
            <span className={`px-3 py-1 text-xs rounded bg-purple-600 text-white`}>{page}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className={`p-1.5 border rounded disabled:opacity-40 transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-[#1a1f3a]' : 'border-gray-300 hover:bg-gray-50'}`}>
              <ChevronRight size={13} />
            </button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
              className={`p-1.5 border rounded disabled:opacity-40 transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-[#1a1f3a]' : 'border-gray-300 hover:bg-gray-50'}`}>
              <ChevronsRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isDark = theme === 'dark';
  const currentUser = getCurrentUser();
  const [tab, setTab] = useState('statuses');

  // Log initial visit + tab changes
  useEffect(() => { logActivity('view', 'settings', null, 'Viewed Settings'); }, []);
  useEffect(() => { logActivity('view', `settings_${tab}`, null, `Viewed Settings — ${tab}`); }, [tab]);

  const TABS = [
    { key: 'statuses',    label: t('settings.tabs.statuses') },
    { key: 'telephony',   label: t('settings.tabs.telephony'), permission: 'Edit Telephony Connections' },
    { key: 'permissions', label: t('settings.tabs.permissions'), permission: 'Edit Permissions' },
    { key: 'whitelist',   label: t('settings.tabs.whitelist'), permission: 'Edit IP Lists' },
    { key: 'blacklist',   label: t('settings.tabs.blacklist'), permission: 'Edit IP Lists' },
    { key: 'sessions',    label: t('settings.tabs.sessions'), permission: 'Edit Sessions' },
    { key: 'logs',        label: t('settings.tabs.logs'), permission: 'Edit Logs' },
  ];
  const visibleTabs = TABS.filter(item => !item.permission || hasPermission(currentUser, item.permission));
  const visibleTabKeys = visibleTabs.map(item => item.key).join('|');

  useEffect(() => {
    if (!visibleTabs.some(item => item.key === tab)) setTab(visibleTabs[0]?.key || 'statuses');
  }, [tab, visibleTabKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabBtn = (active) =>
    `px-5 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
      active ? 'border-purple-500 text-purple-400'
              : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
    }`;

  return (
    <div className="p-6 space-y-4">
      {/* Tab bar */}
      <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className={`flex overflow-x-auto border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          {visibleTabs.map(item => (
            <button key={item.key} onClick={() => setTab(item.key)} className={tabBtn(tab === item.key)}>{item.label}</button>
          ))}
        </div>

        <div className="p-5">
          {tab === 'statuses' && <StatusesTab />}
          {tab === 'permissions' && <PermissionsTab />}
          {tab === 'whitelist' && <IpListTab listType="whitelist" />}
          {tab === 'blacklist' && <IpListTab listType="blacklist" />}
          {tab === 'telephony' && <TelephonyConnectionsTab />}
          {tab === 'sessions' && <SessionsTab />}
          {tab === 'logs' && <LogsTab />}
        </div>
      </div>
    </div>
  );
}
